import { AnimatePresence, motion } from "framer-motion";
import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Icon, type IconName } from "./Icon";
import { useToastStore, type Toast } from "@/store/toasts";
import { toastVariants } from "@/motion/variants";

/* -------------------------------------------------------------------------- */
/* Toasts                                                                     */
/* -------------------------------------------------------------------------- */

function ToastRow({ toast }: { toast: Toast }) {
  const dismiss = useToastStore((s) => s.dismiss);

  useEffect(() => {
    const timer = window.setTimeout(() => dismiss(toast.id), toast.duration);
    return () => window.clearTimeout(timer);
  }, [toast.id, toast.duration, dismiss]);

  return (
    <motion.div
      layout
      className="toast"
      variants={toastVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <span className="toast__icon">
        <Icon name={toast.icon ?? "check"} />
      </span>
      <span className="toast__msg">
        <strong>{toast.title}</strong>
        {toast.message ? <span>{toast.message}</span> : null}
      </span>
      <button
        type="button"
        onClick={() => dismiss(toast.id)}
        aria-label="Dismiss notification"
        style={{
          background: "none",
          border: 0,
          color: "inherit",
          opacity: 0.6,
          padding: 4,
          marginLeft: "auto",
        }}
      >
        <Icon name="close" size={13} />
      </button>
    </motion.div>
  );
}

export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);

  return createPortal(
    <div className="toast-stack" role="status" aria-live="polite">
      <AnimatePresence initial={false}>
        {toasts.map((t) => (
          <ToastRow key={t.id} toast={t} />
        ))}
      </AnimatePresence>
    </div>,
    document.body,
  );
}

/* -------------------------------------------------------------------------- */
/* Empty state                                                                */
/* -------------------------------------------------------------------------- */

interface EmptyStateProps {
  title: string;
  message: string;
  icon?: IconName;
  action?: { label: string; onClick: () => void };
  /** Heading level — 2 when this is the page's main message, 3 inside a grid. */
  level?: 2 | 3;
}

export function EmptyState({ title, message, icon = "search", action, level = 3 }: EmptyStateProps) {
  const Heading = level === 2 ? "h2" : "h3";

  return (
    <motion.div
      className="empty-state"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32 }}
    >
      <span className="empty-state__icon">
        <Icon name={icon} />
      </span>
      <Heading>{title}</Heading>
      <p>{message}</p>
      {action ? (
        <button type="button" className="btn btn--ghost btn--sm mt-4" onClick={action.onClick}>
          {action.label}
        </button>
      ) : null}
    </motion.div>
  );
}

/* -------------------------------------------------------------------------- */
/* Loading                                                                    */
/* -------------------------------------------------------------------------- */

export function Skeleton({
  height = 200,
  radius = "var(--radius-lg)",
  className,
}: {
  height?: number | string;
  radius?: string;
  className?: string;
}) {
  return (
    <div
      className={className}
      aria-hidden="true"
      style={{
        height,
        borderRadius: radius,
        background:
          "linear-gradient(100deg, var(--surface-3) 20%, var(--surface-2) 40%, var(--surface-3) 60%)",
        backgroundSize: "300% 100%",
        animation: "skeletonShift 1.5s ease-in-out infinite",
      }}
    />
  );
}

export function CardGridSkeleton({ count = 6, height = 380 }: { count?: number; height?: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <Skeleton key={i} height={height} />
      ))}
    </>
  );
}

/** Full-section loader used while a route's primary query resolves. */
export function SectionLoader({ label = "Loading" }: { label?: string }) {
  return (
    <div className="flex-center" style={{ padding: "clamp(48px, 9vw, 110px) 0" }} role="status">
      <motion.span
        style={{
          width: 34,
          height: 34,
          borderRadius: "50%",
          border: "2.5px solid var(--border)",
          borderTopColor: "var(--green)",
          display: "block",
        }}
        animate={{ rotate: 360 }}
        transition={{ duration: 0.75, repeat: Infinity, ease: "linear" }}
      />
      <span className="sr-only">{label}</span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Error                                                                      */
/* -------------------------------------------------------------------------- */

export function ErrorState({
  title = "Something went wrong",
  message,
  onRetry,
}: {
  title?: string;
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <EmptyState
      title={title}
      message={message ?? "We could not load this just now. Please try again."}
      icon="alert"
      {...(onRetry ? { action: { label: "Try again", onClick: onRetry } } : {})}
    />
  );
}

export function ResultsCount({ children }: { children: ReactNode }) {
  return (
    <p className="results-count" aria-live="polite">
      {children}
    </p>
  );
}
